import React, { useEffect, useRef, useState } from 'react';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { Check, Clock, Loader2, MessageSquare, Undo2, X, FileText, Upload, History, AlertTriangle } from 'lucide-react';
import { Avatar } from '../ui/Avatar';
import { StatusBadge } from '../ui/StatusBadge';
import { useApp } from '../../context/AppContext';
import apiService from '../../services/api';
import toast from 'react-hot-toast';

const NAVY = 'var(--text)';
const todayIso = () => format(new Date(), 'yyyy-MM-dd');
const endOfLeave = (r) => String(r.to ?? r.from ?? '').slice(0, 10);
const HISTORY_WORD = {
  created: 'Booked', approved: 'Approved', rejected: 'Rejected', withdrawn: 'Withdrawn',
  cancelled: 'Cancelled', deleted: 'Deleted', document_uploaded: 'Sick note added',
};
const DEFAULT_TYPE_COLOR = '#ffc81e';

// ─── Helpers ─────────────────────────────────────────────────
const dateOnly = (value) => (value ? String(value).slice(0, 10) : '');

function isHalfDayRequest(request) {
  if (!request) return false;
  if (request.halfDay === true || request.isHalfDay === true) return true;
  if (request.halfDayPeriod) return true;
  if (request.period === 'AM' || request.period === 'PM') return true;
  if (request.days !== undefined && request.days !== null && parseFloat(request.days) === 0.5) return true;
  return false;
}

function getHalfDayPeriod(request) {
  const period = request?.halfDayPeriod ?? request?.period ?? null;
  if (!period) return null;
  const p = String(period).toLowerCase();
  if (p === 'am' || p === 'morning') return 'AM';
  if (p === 'pm' || p === 'afternoon') return 'PM';
  return null;
}

/** "6 – 9 Oct 2026", "28 Sep – 2 Oct 2026" or "Tue 6 Oct 2026" */
function formatRange(fromStr, toStr) {
  const from = parseISO(fromStr);
  const to = parseISO(toStr || fromStr);
  if (fromStr === toStr || !toStr) return format(from, 'EEE d MMM yyyy');
  if (from.getFullYear() !== to.getFullYear()) return `${format(from, 'd MMM yyyy')} – ${format(to, 'd MMM yyyy')}`;
  if (from.getMonth() === to.getMonth()) return `${format(from, 'd')} – ${format(to, 'd MMM yyyy')}`;
  return `${format(from, 'd MMM')} – ${format(to, 'd MMM yyyy')}`;
}

/** Second line under the dates: when it is, relative to today. */
function whenLabel(fromStr, toStr) {
  const today = new Date();
  const start = differenceInCalendarDays(parseISO(fromStr), today);
  const end = differenceInCalendarDays(parseISO(toStr || fromStr), today);
  if (start <= 0 && end >= 0) return { text: 'On leave now', tone: 'now' };
  if (start === 1) return { text: 'Starts tomorrow', tone: 'soon' };
  if (start > 1 && start <= 14) return { text: `Starts in ${start} days`, tone: 'soon' };
  if (start > 14) return { text: format(parseISO(fromStr), "EEEE"), tone: 'later' };
  if (end === -1) return { text: 'Ended yesterday', tone: 'past' };
  return { text: `Ended ${format(parseISO(toStr || fromStr), 'd MMM')}`, tone: 'past' };
}

const WHEN_STYLE = {
  now:   'text-[color:var(--ok-text)]',
  soon:  'text-[color:var(--warn-text)]',
  later: 'text-sand-500',
  past:  'text-sand-400',
};

// ─── Small pieces ────────────────────────────────────────────
function TypePill({ type, color }) {
  const c = color || DEFAULT_TYPE_COLOR;
  return (
    <span
      className="inline-flex max-w-[180px] items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: `${c}33`, color: 'var(--text)' }}
      title={type}
    >
      <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: c }} />
      <span className="truncate">{type}</span>
    </span>
  );
}

function DurationPill({ request }) {
  if (request.period === 'custom' || (request.startTime && request.endTime)) {
    const hours = parseFloat(request.hours);
    return (
      <span className="inline-flex flex-col">
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-sand-900">
          <Clock size={12} className="text-sand-400" />
          {Number.isFinite(hours) ? `${hours % 1 === 0 ? hours : hours.toFixed(1)}h` : 'Hours'}
        </span>
        <span className="text-xs text-sand-400">
          {request.startTime || '?'} – {request.endTime || '?'}
        </span>
      </span>
    );
  }

  if (isHalfDayRequest(request)) {
    const period = getHalfDayPeriod(request);
    return (
      <span className="inline-flex items-center overflow-hidden rounded-full border border-[color:var(--warn-border)] bg-[color:var(--warn-bg)] text-xs font-semibold text-[color:var(--warn-text)]">
        <span className="px-2 py-0.5">½ day</span>
        {period && <span className="bg-[#FFC81E] px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[#1C1A14]">{period}</span>}
      </span>
    );
  }

  const d = parseFloat(request.days);
  if (!Number.isFinite(d)) return <span className="text-sm text-sand-400">—</span>;
  return (
    <span className="text-sm font-semibold text-sand-900">
      {d % 1 === 0 ? d : d.toFixed(1)}
      <span className="ml-1 font-normal text-sand-500">{d === 1 ? 'day' : 'days'}</span>
    </span>
  );
}

function Spinner({ size = 13 }) {
  return <Loader2 size={size} className="animate-spin" aria-hidden="true" />;
}

const personName = (p) => (p ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() : '');

// ─── Row ─────────────────────────────────────────────────────
// onDeleted(id)  — called after the request is withdrawn / cancelled, when the
//                  list hides closed requests (it's kept on the server, with history)
// onUpdated(req) — called after approve / reject / withdraw so the parent updates it in place
// typeColor      — the leave type's colour from company settings (optional)
export function RequestRow({ request, showEmployee = true, typeColor, onUpdated, onDeleted }) {
  const { getEmployee, updateStatus, currentUser, isAdmin, canApprove, notifyLeaveChanged } = useApp();

  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showReject, setShowReject] = useState(false);
  // Approving would take them beyond their balance: the server's warning
  const [overWarn, setOverWarn] = useState(null);
  // Known before clicking: the server checks requests waiting for my decision
  const overBalance = request.status === 'pending' && !!request.balanceCheck?.overdraw;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [history, setHistory] = useState(null);
  const rejectInput = useRef(null);

  useEffect(() => {
    if (showReject) rejectInput.current?.focus();
  }, [showReject]);

  // The delete confirmation quietly cancels itself after a few seconds.
  useEffect(() => {
    if (!confirmDelete) return undefined;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const empId = request.empId ?? request.userId;
  const emp = request.employee ?? request.emp ?? getEmployee?.(empId) ?? { id: empId, name: 'Unknown employee' };
  const empName = emp.name ?? (`${emp.firstName ?? ''} ${emp.lastName ?? ''}`.trim() || 'Unknown employee');
  const empDept = emp.department?.name ?? emp.dept ?? '';

  const from = dateOnly(request.from);
  const to = dateOnly(request.to || request.from);
  const when = from ? whenLabel(from, to) : null;

  const isPending = request.status === 'pending';
  const isOwn = currentUser?.id && currentUser.id === empId;
  // Company admins, the person's manager, or their named approver — never your own
  const canDecide = isPending && !isOwn && (canApprove ? canApprove(empId) : isAdmin);
  // Withdraw your own request; approvers can cancel leave they look after.
  // Nothing is deleted: the request is kept as withdrawn / cancelled, with a history.
  const open = ['pending', 'approved'].includes(request.status) && endOfLeave(request) >= todayIso();
  // Office shutdown bookings: only company admins can take someone out of one
  const fromShutdown = !!request.shutdownId;
  const canWithdraw = open && !request.masked && (fromShutdown ? isAdmin : (isOwn || (canApprove ? canApprove(empId) : isAdmin)));
  const withdrawWord = fromShutdown ? 'Take out of shutdown' : isOwn ? 'Withdraw' : 'Cancel leave';
  const busy = approving || rejecting || deleting;
  const columns = showEmployee ? 7 : 6;
  const approverName = personName(request.approver);
  const reviewerName = personName(request.reviewer);
  const canSeeDoc = isOwn || isAdmin;

  // ── Approve ──────────────────────────────────────────────
  async function handleApprove({ anyway = false } = {}) {
    if (busy) return;
    setApproving(true);
    try {
      const res = await apiService.approveLeaveRequest(request.id, '', anyway ? { allowOverdraw: true } : {});
      const updated = res?.data ?? res;
      toast.success(`Approved ${empName}'s request${anyway ? ' (beyond their balance)' : ''}`);
      setOverWarn(null);
      updateStatus?.(request.id, 'approved');
      onUpdated?.({ ...request, ...updated, status: 'approved' });
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.code === 'OVER_BALANCE') {
        setShowReject(false);
        setOverWarn(data.check?.message || data.message);
      } else {
        toast.error(data?.message || "The request couldn't be approved.");
      }
    } finally {
      setApproving(false);
    }
  }

  // ── Reject ───────────────────────────────────────────────
  async function handleReject() {
    if (busy) return;
    setRejecting(true);
    try {
      const res = await apiService.rejectLeaveRequest(request.id, rejectNote.trim());
      const updated = res?.data ?? res;
      toast.success(`Rejected ${empName}'s request`);
      updateStatus?.(request.id, 'rejected');
      onUpdated?.({ ...request, ...updated, status: 'rejected' });
      setShowReject(false);
      setRejectNote('');
    } catch (err) {
      toast.error(err?.response?.data?.message || "The request couldn't be rejected.");
    } finally {
      setRejecting(false);
    }
  }

  function closeReject() {
    setShowReject(false);
    setRejectNote('');
  }

  // ── Withdraw / cancel (two clicks; kept with an audit trail) ──
  async function handleDelete() {
    if (busy) return;
    setDeleting(true);
    try {
      const res = await apiService.withdrawLeaveRequest(request.id);
      toast.success(res?.message || (isOwn ? 'Request withdrawn' : 'Leave cancelled'));
      updateStatus?.(request.id, 'cancelled');
      onUpdated?.({ ...request, status: 'cancelled' });
      notifyLeaveChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || `The request couldn't be ${isOwn ? 'withdrawn' : 'cancelled'}.`);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  async function uploadDoc(file) {
    if (!file) return;
    setUploading(true);
    try {
      await apiService.uploadLeaveDocument(request.id, file);
      toast.success('Sick note added. Only HR can see it.');
      onUpdated?.({ ...request, documentName: file.name, documentUploadedAt: new Date().toISOString() });
    } catch (err) {
      toast.error(err?.response?.data?.message || "The document couldn't be uploaded.");
    } finally {
      setUploading(false);
    }
  }

  async function openDoc() {
    try {
      const blob = await apiService.getLeaveDocument(request.id);
      const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.response?.data?.message || "The document couldn't be opened.");
    }
  }

  async function toggleHistory() {
    if (history) { setHistory(null); return; }
    try {
      const res = await apiService.getLeaveRequestHistory(request.id);
      setHistory(res?.data ?? []);
    } catch {
      setHistory([]);
    }
  }

  return (
    <>
      <tr
        className={`group transition-colors ${showReject ? 'bg-[color:var(--bad-bg)]' : 'hover:bg-sand-50'} ${
          deleting ? 'opacity-50' : ''
        }`}
      >
        {/* Employee */}
        {showEmployee && (
          <td className="rq-sticky-l relative py-3 pl-5 pr-4">
            {/* Pending rows get an amber edge so they stand out in long lists */}
            {isPending && (
              <span className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-[#FFC81E]" aria-hidden="true" />
            )}
            <div className="flex items-center gap-3">
              <Avatar employee={emp} size="md" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold" style={{ color: NAVY }}>
                  {empName}
                  {isOwn && <span className="ml-1.5 text-xs font-medium text-sand-400">(you)</span>}
                </p>
                <p className="truncate text-xs text-sand-400">{empDept || 'No department'}</p>
              </div>
            </div>
          </td>
        )}

        {/* Type */}
        <td className="px-4 py-3">
          <TypePill type={request.type} color={typeColor} />
        </td>

        {/* Dates */}
        <td className="whitespace-nowrap px-4 py-3">
          {from ? (
            <>
              <p className="text-sm font-medium text-sand-800">{formatRange(from, to)}</p>
              {when && <p className={`text-xs ${WHEN_STYLE[when.tone]}`}>{when.text}</p>}
            </>
          ) : (
            <span className="text-sm text-sand-400">—</span>
          )}
        </td>

        {/* Duration */}
        <td className="whitespace-nowrap px-4 py-3">
          <DurationPill request={request} />
        </td>

        {/* Status */}
        <td className="px-4 py-3">
          <StatusBadge status={request.status === 'cancelled' ? 'withdrawn' : (request.status || 'pending')} />
          {isPending && approverName && (
            <p className="mt-1 max-w-[170px] truncate text-xs text-sand-400" title={`Waiting for ${approverName}`}>Waiting for {approverName}</p>
          )}
          {fromShutdown ? (
            <p className="mt-1 max-w-[170px] truncate text-xs text-sand-400" title={request.note || 'Office shutdown'}>Office shutdown</p>
          ) : request.status === 'approved' && reviewerName && !request.masked && (
            <p className="mt-1 max-w-[170px] truncate text-xs text-sand-400">by {reviewerName}</p>
          )}
          {(request.overdrawn || (overBalance && canDecide)) && !request.masked && (
            <p className="mt-1 text-xs font-semibold text-[color:var(--bad-text)]">{request.overdrawn ? 'Beyond balance' : 'Over balance'}</p>
          )}
          {request.status === 'rejected' && request.reviewNote && (
            <p className="mt-1 max-w-[160px] truncate text-xs text-[color:var(--bad-text)]" title={request.reviewNote}>
              {request.reviewNote}
            </p>
          )}
        </td>

        {/* Note */}
        <td className="px-4 py-3">
          {request.note ? (
            <span
              className="inline-flex max-w-[180px] items-start gap-1.5 text-xs text-sand-500"
              title={request.note}
            >
              <MessageSquare size={12} className="mt-0.5 flex-shrink-0 text-sand-300" />
              <span className="line-clamp-2">{request.note}</span>
            </span>
          ) : !(request.documentRequired || request.documentName) ? (
            <span className="text-xs text-sand-300">—</span>
          ) : null}
          {(request.documentRequired || request.documentName) && !request.masked && (
            <div className="mt-1">
              {request.documentName ? (
                canSeeDoc ? (
                  <button type="button" onClick={openDoc} className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--accent)]">
                    <FileText size={12} /> Sick note
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-sand-500"><FileText size={12} /> Sick note with HR</span>
                )
              ) : isOwn || isAdmin ? (
                <label className="inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-[color:var(--warn-text)]">
                  {uploading ? <Spinner size={12} /> : <Upload size={12} />} Add sick note
                  <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => uploadDoc(e.target.files?.[0])} />
                </label>
              ) : (
                <span className="text-xs font-semibold text-[color:var(--warn-text)]">Sick note needed</span>
              )}
            </div>
          )}
        </td>

        {/* Actions (pinned to the right edge so they never scroll out of view) */}
        <td className="rq-sticky-r px-4 py-3">
          <div className="flex items-center justify-end gap-1.5" style={{ minWidth: canDecide ? 190 : 72 }}>
            {canDecide && !confirmDelete && (
              <>
                <button
                  type="button"
                  onClick={() => handleApprove()}
                  disabled={busy || !!overWarn}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-emerald-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {approving ? <Spinner size={12} /> : <Check size={13} strokeWidth={2.5} />}
                  {approving ? 'Approving…' : 'Approve'}
                </button>
                <button
                  type="button"
                  onClick={() => (showReject ? closeReject() : setShowReject(true))}
                  disabled={busy}
                  aria-expanded={showReject}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] border px-3 py-1.5 text-xs font-semibold transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 text-[color:var(--bad-text)] ${
                    showReject
                      ? 'border-[color:var(--bad-text)] bg-[color:var(--bad-bg)]'
                      : 'border-[color:var(--field-border)] bg-[color:var(--surface)] hover:bg-[color:var(--bad-bg)]'
                  }`}
                >
                  <X size={13} strokeWidth={2.5} />
                  Reject
                </button>
              </>
            )}

            {canWithdraw && !canDecide &&
              (confirmDelete ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <span className="text-xs font-medium text-sand-500">{isOwn ? 'Withdraw?' : 'Cancel this leave?'}</span>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="inline-flex items-center gap-1 rounded-[10px] bg-red-600 px-2.5 py-1.5 text-xs font-semibold text-white transition-all hover:bg-red-700 active:scale-95"
                  >
                    {deleting && <Spinner size={12} />}
                    {withdrawWord}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                    className="rounded-lg px-2 py-1.5 text-xs font-medium text-sand-500 hover:bg-sand-100"
                  >
                    Keep
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  title={isOwn ? 'Withdraw this request (it stays in the history)' : 'Cancel this leave (it stays in the history)'}
                  aria-label={`${withdrawWord}: ${empName}'s request`}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-[10px] border border-[color:var(--field-border)] px-2.5 py-1.5 text-xs font-semibold text-sand-500 transition-all hover:bg-[color:var(--bad-bg)] hover:text-[color:var(--bad-text)] disabled:cursor-not-allowed"
                >
                  <Undo2 size={13} /> {withdrawWord}
                </button>
              ))}
            {!request.masked && (isOwn || isAdmin || canDecide) && (
              <button type="button" onClick={toggleHistory} title="History" aria-label="Show history" aria-expanded={!!history}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] text-sand-400 hover:bg-sand-100">
                <History size={14} />
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* History (audit trail) */}
      {history && (
        <tr>
          <td colSpan={columns} className="px-5 pb-3 pt-0">
            <div className="rq-pinned-panel rounded-[12px] border border-[color:var(--border)] bg-[color:var(--surface-2)] px-3 py-2 text-xs text-sand-500">
              {history.length === 0 ? 'No history recorded for this request yet.' : history.map((h) => (
                <div key={h.id} className="flex gap-2 py-0.5">
                  <span className="w-[150px] flex-shrink-0">{format(new Date(h.createdAt), 'd MMM yyyy, HH:mm')}</span>
                  <span><b className="text-sand-700">{HISTORY_WORD[h.action] ?? h.action}</b>{h.actor ? ` by ${personName(h.actor)}` : ''}{h.note ? ` · ${h.note}` : ''}</span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}

      {/* Waiting for my decision and it would take them over their balance:
          say so up front, before Approve is clicked */}
      {overBalance && canDecide && !overWarn && !showReject && (
        <tr className="bg-[color:var(--warn-bg-soft)]">
          <td colSpan={columns} className="px-5 pb-3 pt-0">
            <div role="note" className="rq-pinned-panel flex items-start gap-2 rounded-[12px] border border-[color:var(--warn-border)] bg-[color:var(--surface)] px-3 py-2 text-[13px] text-[color:var(--warn-text)]">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                <span className="font-semibold">Over their balance.</span> {request.balanceCheck.message}{' '}
                Talk to {empName.split(' ')[0]} before approving.
              </span>
            </div>
          </td>
        </tr>
      )}

      {/* Approving would take them beyond their balance */}
      {overWarn && canDecide && (
        <tr className="bg-[color:var(--warn-bg-soft)]">
          <td colSpan={columns} className="px-5 pb-4 pt-0">
            <div role="alert" className="rq-pinned-panel flex flex-wrap items-center gap-3 rounded-[16px] border border-[color:var(--warn-border)] bg-[color:var(--surface)] p-3 shadow-sm">
              <div className="flex min-w-[220px] flex-1 items-start gap-2 text-sm text-[color:var(--warn-text)]">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span><span className="font-semibold">Beyond their balance.</span> {overWarn}</span>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setOverWarn(null)} disabled={approving} className="btn-ghost">
                  Don’t approve
                </button>
                <button
                  type="button"
                  onClick={() => handleApprove({ anyway: true })}
                  disabled={approving}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[12px] bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {approving ? <Spinner /> : <Check size={14} strokeWidth={2.5} />}
                  {approving ? 'Approving…' : 'Approve anyway'}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* Reject reason */}
      {showReject && canDecide && (
        <tr className="bg-[color:var(--bad-bg)]">
          <td colSpan={columns} className="px-5 pb-4 pt-0">
            {/* Stays in view however far the table is scrolled sideways */}
            <div className="rq-pinned-panel flex flex-wrap items-center gap-3 rounded-[16px] border border-[color:var(--border)] bg-[color:var(--surface)] p-3 shadow-sm">
              <div className="min-w-[200px] flex-1">
                <label htmlFor={`reject-${request.id}`} className="mb-1 block text-xs font-semibold text-[color:var(--bad-text)]">
                  Reason for rejecting <span className="font-normal text-sand-400">(optional, {empName.split(' ')[0]} will see it)</span>
                </label>
                <input
                  id={`reject-${request.id}`}
                  ref={rejectInput}
                  type="text"
                  value={rejectNote}
                  maxLength={500}
                  onChange={(e) => setRejectNote(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleReject();
                    if (e.key === 'Escape') closeReject();
                  }}
                  placeholder="e.g. Two people from Sales are already off that week"
                  className="form-input"
                />
              </div>
              <div className="flex items-center gap-2 self-end">
                <button type="button" onClick={closeReject} disabled={rejecting} className="btn-ghost">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={rejecting}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[12px] bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-red-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {rejecting ? <Spinner /> : <X size={14} strokeWidth={2.5} />}
                  {rejecting ? 'Rejecting…' : 'Reject request'}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}